// ===========================================================================
// The keepsake, client side: what Lumi remembers on this device for somebody
// without an account.
//
// The server issues a random key once the age gate has passed (see
// server/src/keepsakes.js). It lives in localStorage, goes back with every
// /api/session call, and that is all it does: it holds no name and no personal
// detail, and clearing it is the whole of "forget me on this device". Every
// storage access is wrapped, because localStorage is missing or throws in
// private windows, some embedded browsers, and the thumbnail capture.
//
// No config import here on purpose: this module runs under node --test, and
// config.js reads import.meta.env. Callers pass the server base in. ASCII only.
// ===========================================================================

const KEEPSAKE_KEY = 'lumi.keepsake';

function valid(v) {
  return !!v && typeof v === 'object' && typeof v.id === 'string' && /^ks_/.test(v.id) && typeof v.key === 'string' && /^kk_/.test(v.key);
}

/** @returns {{ id: string, key: string }|null} */
export function getKeepsake() {
  try {
    const v = JSON.parse(localStorage.getItem(KEEPSAKE_KEY) || 'null');
    return valid(v) ? { id: v.id, key: v.key } : null;
  } catch {
    return null;
  }
}

/** Keep what the server just issued. Anything malformed is ignored, never half-stored. */
export function storeKeepsake(keepsake) {
  if (!valid(keepsake)) return false;
  try {
    localStorage.setItem(KEEPSAKE_KEY, JSON.stringify({ id: keepsake.id, key: keepsake.key }));
    return true;
  } catch {
    return false;
  }
}

export function clearKeepsake() {
  try {
    localStorage.removeItem(KEEPSAKE_KEY);
  } catch {
    /* nothing to clear, or nowhere to clear it from */
  }
}

/**
 * "Forget me on this device": tell the server to revoke the key and run every
 * cascade behind it, then clear it here. Storage is cleared even when the
 * request fails -- the device forgets regardless, and a key the server never
 * heard from again is swept after ninety days.
 * @param {{ base?: string, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<boolean>} whether the server confirmed
 */
export async function forgetKeepsake({ base = '', fetchImpl = globalThis.fetch } = {}) {
  const ks = getKeepsake();
  let confirmed = false;
  if (ks && typeof fetchImpl === 'function') {
    try {
      const res = await fetchImpl(`${base}/api/keepsake/forget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: ks.key }),
      });
      confirmed = !!res && res.ok === true;
    } catch {
      confirmed = false;
    }
  }
  clearKeepsake();
  return confirmed;
}

export default { getKeepsake, storeKeepsake, clearKeepsake, forgetKeepsake };
